/**
 * Debug Logging Utilities for Auth Portal
 *
 * Provides consistent, namespaced console logging for debugging.
 * All debug logs are prefixed with [Namespace] for easy filtering in console.
 *
 * @module utils/debug
 */

import { getErrorMessage } from './error';

/**
 * Environment check - logs are always enabled in development
 */
function isDebugEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  if (import.meta.env.DEV) return true;
  try {
    return localStorage.getItem('DEBUG_ENABLED') === 'true';
  } catch {
    return false;
  }
}

/**
 * Format error for console output with full details
 */
function formatError(error: unknown): {
  message: string;
  details: Record<string, unknown>;
} {
  const message = getErrorMessage(error, 'Unknown error');
  const details: Record<string, unknown> = {};

  if (error && typeof error === 'object') {
    const err = error as Record<string, unknown>;

    if ('data' in err) details.data = err.data;
    if ('shape' in err) {
      const shape = err.shape as Record<string, unknown>;
      details.code = shape?.code;
      const shapeData = shape?.data as Record<string, unknown> | undefined;
      details.httpStatus = shapeData?.httpStatus;
    }
    if ('response' in err) {
      const response = err.response as Record<string, unknown>;
      details.status = response?.status;
      details.statusText = response?.statusText;
      details.responseData = response?.data;
    }
    if ('cause' in err) details.cause = err.cause;
    if ('stack' in err && typeof err.stack === 'string') details.stack = err.stack;
  }

  return { message, details };
}

export interface DebugLogger {
  error: (message: string, error?: unknown, context?: Record<string, unknown>) => void;
  warn: (message: string, context?: Record<string, unknown>) => void;
  info: (message: string, context?: Record<string, unknown>) => void;
  debug: (message: string, context?: Record<string, unknown>) => void;
}

/**
 * Create a namespaced debug logger
 */
export function createDebugLogger(namespace: string): DebugLogger {
  const prefix = `[${namespace}]`;

  return {
    error: (message: string, error?: unknown, context?: Record<string, unknown>) => {
      if (!isDebugEnabled()) return;

      const timestamp = new Date().toISOString();
      console.error(`${prefix} ${message}`);

      if (error) {
        const { message: errorMessage, details } = formatError(error);
        console.error(`${prefix} Error: ${errorMessage}`);
        if (Object.keys(details).length > 0) {
          console.error(`${prefix} Error Details:`, details);
        }
      }

      if (context && Object.keys(context).length > 0) {
        console.error(`${prefix} Context:`, context);
      }

      console.error(`${prefix} Timestamp: ${timestamp}`);
    },

    warn: (message: string, context?: Record<string, unknown>) => {
      if (!isDebugEnabled()) return;
      console.warn(`${prefix} ${message}`);
      if (context && Object.keys(context).length > 0) {
        console.warn(`${prefix} Context:`, context);
      }
    },

    info: (message: string, context?: Record<string, unknown>) => {
      if (!isDebugEnabled()) return;
      console.info(`${prefix} ${message}`);
      if (context && Object.keys(context).length > 0) {
        console.info(`${prefix} Details:`, context);
      }
    },

    debug: (message: string, context?: Record<string, unknown>) => {
      if (!isDebugEnabled()) return;
      console.debug(`${prefix} ${message}`);
      if (context && Object.keys(context).length > 0) {
        console.debug(`${prefix} Details:`, context);
      }
    },
  };
}

// Enable debug logging in production via browser console
if (typeof window !== 'undefined') {
  (window as unknown as { enableDebugLogs: () => void }).enableDebugLogs = () => {
    localStorage.setItem('DEBUG_ENABLED', 'true');
    console.info('[Debug] Debug logging enabled. Refresh the page to see logs.');
  };

  (window as unknown as { disableDebugLogs: () => void }).disableDebugLogs = () => {
    localStorage.removeItem('DEBUG_ENABLED');
    console.info('[Debug] Debug logging disabled.');
  };
}
